import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SHOWCASES } from 'components/minute/layout/CreateMinuteBody/showcases';
import { IconTextButtonBlock } from 'components/minute/view/IconTextButtonBlock';
import { ImageMask } from 'elements/visual/ImageMask';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EBorders, EColors, EFonts, EGrads, EHovers, EShadows } from 'libs/styles/styling';
import React from 'react';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
}

export const CreateShowcase = (props: Props): JSX.Element => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const bgMotion = {
        rest: {
            backgroundColor: 'rgba(0,0,0,0.01)',
            ease: 'easeOut',
            duration: 0.2,
            type: 'tween'
        },
        hover: {
            backgroundColor: 'rgba(0,0,0,0.35)',
            transition: {
                duration: 0.4,
                type: 'tween',
                ease: 'easeIn'
            }
        }
    };

    const visibilityMotion = {
        rest: {
            opacity: 0,
            ease: 'easeOut',
            duration: 0.2,
            type: 'tween'
        },
        hover: {
            opacity: 1,
            transition: {
                duration: 0.4,
                type: 'tween',
                ease: 'easeIn'
            }
        }
    };

    return (
        <S_CreateShowcase className={props.className}>
            <S_Items>
                {SHOWCASES.map((showcase, index) => {
                    return (
                        <S_Item
                            key={showcase.title}
                            $selectedIndex={selectedIndex === index}
                            onClick={() => {
                                setSelectedIndex(index);
                            }}
                            {...conditionalScaleAnimation(true, 1.05, 1.05)}
                        >
                            <S_Icon
                                $selectedIndex={selectedIndex === index}
                                icon={[showcase.iconSet, showcase.iconName]}
                            />
                            <S_Text $selectedIndex={selectedIndex === index}>
                                {showcase.title}
                            </S_Text>
                            {selectedIndex === index && (
                                <ImageMask
                                    randomizePosition={true}
                                    step={20}
                                    image="contour"
                                    repeat={true}
                                />
                            )}
                        </S_Item>
                    );
                })}
            </S_Items>
            <S_Demos>
                {SHOWCASES[selectedIndex].demos.map((demo) => {
                    return (
                        <S_Demo
                            key={demo.link}
                            $background={demo.img}
                            onClick={() => {
                                window.open(demo.link, '_blank');
                            }}
                            {...conditionalScaleAnimation(true, 1.02, 1.02)}
                            initial="rest"
                            whileHover="hover"
                            animate="rest"
                        >
                            <S_Dark variants={bgMotion}>
                                <motion.div variants={visibilityMotion}>
                                    <S_IconTextButtonBlock
                                        iconName="hand-pointer"
                                        title={demo.title}
                                        subtitle={demo.desc}
                                        buttonText={'Open'}
                                        buttonAction={() => {
                                            console.log('');
                                        }}
                                    />
                                </motion.div>
                            </S_Dark>
                        </S_Demo>
                    );
                })}
                {SHOWCASES[selectedIndex].demos.length % 2 === 1 && <S_Placeholder />}
            </S_Demos>
        </S_CreateShowcase>
    );
};

const S_CreateShowcase = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Items = styled.div`
    display: flex;
    justify-content: center;
`;

const S_Item = styled(motion.div)<{ $selectedIndex: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 25px 10px 18px 10px;
    min-width: 115px;
    width: fit-content;
    justify-content: center;
    border-radius: 7px;
    box-shadow: ${EShadows.LIGHT};
    position: relative;
    flex-shrink: 0;
    user-select: none;
    display: flex;
    margin: 0 10px;
    justify-content: center;
    ${(props) => {
        if (props.$selectedIndex) {
            return css`
                background: ${EGrads.ACC2_GRAD};
                transform: scale(1.05) !important;
            `;
        } else {
            return css`
                cursor: pointer;
                background: ${EGrads.WHITE_GRAD};
                &:hover {
                    box-shadow: ${EShadows.LIGHT}, ${EHovers.DARK1};
                }
            `;
        }
    }}
`;

const S_Icon = styled(FontAwesomeIcon)<{ $selectedIndex: boolean }>`
    font-size: 22px;
    color: ${(props) => (props.$selectedIndex ? EColors.WHITE : EColors.DARK)};
`;

const S_Text = styled.div<{ $selectedIndex: boolean }>`
    text-align: center;
    font-family: ${EFonts.Medium};
    white-space: nowrap;
    padding: 15px 15px 0 15px;
    font-size: 16px;
    color: ${(props) => (props.$selectedIndex ? EColors.WHITE : EColors.DARK)};
`;

const S_IconTextButtonBlock = styled(IconTextButtonBlock)`
    margin: 0 0 30px 0;
    width: 250px;
    background-color: #efefef;
    border-radius: 7px;
    box-shadow: 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
    border: 1px solid ${EBorders.DARK};
`;

const S_Demos = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 50px 0 0 0;
    justify-content: space-around;
    min-width: 1260px;
`;

const S_Demo = styled(motion.div)<{ $background: string }>`
    width: 600px;
    height: 371px;
    border-radius: 7px;
    background-color: #efefef;
    display: flex;
    justify-content: center;
    align-items: center;
    background-image: url(${(props) => props.$background});
    background-size: cover;
    background-position: center;
    margin: 0 0 30px 0;
    cursor: pointer;
    flex-shrink: 0;
    position: relative;
`;

const S_Dark = styled(motion.div)`
    width: 100%;
    height: 100%;
    border-radius: 7px;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    top: 0;
    left: 0;
`;

const S_Placeholder = styled.div`
    width: 600px;
    height: 371px;
`;
